<script lang="ts" setup>
import LineXClose from '@docue/docue-ui-v2/icons/LineXClose.vue'
import LineAlertCircle from '@docue/docue-ui-v2/icons/LineAlertCircle.vue'

import type { ActivationCompletedParams, ActivationFailureParams, PlanSelectedParams } from '~/composables/subscriptionProcess'

import logoDocueRed from '~/assets/images/logo_docue_red.svg?url'

import SubscriptionCart from './SubscriptionCart.vue'
import SubscriptionPlans from './SubscriptionPlans.vue'

const emit = defineEmits<{
  checkoutStarted: []
  planSelected: [params: PlanSelectedParams]
  activationFailed: [params: ActivationFailureParams]
  activationCompleted: [params: ActivationCompletedParams]
  showIntercom: []
  notifyContactFormTracker: []
}>()

const { t } = useI18n()
const { country, workspace, isCurrentUserAdmin } = useAuth()

const isOpen = defineModel({ default: false, required: true })

const priceListName = computed(() => country.value === 'FI' ? 'current' : 'pricing-test')

const {
  stage,
  selectedPlan,
  onPlanSelected,
  onActivationCompleted,
  onActivationFailed,
  onShowIntercom,
  onNotifyContactFormTracker,
  priceList,
} = useSubscriptionProcessContextProvider({ priceListName })

const { isMainSubscriptionPaused, isMainSubscriptionSetup, isMainSubscriptionStartingInFuture } = useSubscription()

const priceListData = computed(() => priceList.data.value)

const subscriptionManagementRestrictedReason = computed(() => {
  switch (true) {
    case workspace.value?.attributes.hasParentCompany:
      return t('subscribe.restricted.sub-account')
    case isMainSubscriptionPaused.value:
      return t('subscribe.restricted.paused')
    case isMainSubscriptionSetup.value:
      return t('subscribe.restricted.setup')
    case isMainSubscriptionStartingInFuture.value:
      return t('subscribe.restricted.future')
    case !isCurrentUserAdmin.value:
      return t('subscribe.restricted.admin')
    case workspace.value?.attributes.hasCustomPricing:
      return t('subscribe.restricted.custom-pricing')
    default:
      return undefined
  }
})

onPlanSelected((params) => {
  emit('planSelected', params)
})

onActivationCompleted((params) => {
  emit('activationCompleted', params)
})

onActivationFailed((params) => {
  emit('activationFailed', params)
})

onShowIntercom(() => {
  emit('showIntercom')
})

onNotifyContactFormTracker(() => {
  emit('notifyContactFormTracker')
})

watch(isOpen, () => {
  if (isOpen.value)
    emit('checkoutStarted')
}, { immediate: true })
</script>

<template>
  <DTModal
    v-model="isOpen"
    size="fs"
    container-class="relative"
  >
    <template v-if="stage === 'plans'">
      <DTIconButton
        class="absolute left-4 top-4 z-[999]"
        @click="isOpen = false"
      >
        <LineXClose class="size-5" />
      </DTIconButton>
      <SubscriptionPlans>
        <div
          v-if="subscriptionManagementRestrictedReason"
          class="
              flex
              h-[576px]
              items-center
              justify-center
              rounded-xl
              border
              border-gray-200
              bg-white
              "
        >
          <DTAlert variant="blue">
            <LineAlertCircle class="size-4 shrink-0" />
            {{ subscriptionManagementRestrictedReason }}
          </DTAlert>
        </div>
        <SubscriptionPlanCard
          v-else-if="priceListData?.data_version === 'v2'"
        />
        <SubscriptionPlansTable
          v-else
          class="rounded-xl bg-white"
        />
      </SubscriptionPlans>
    </template>

    <TopBarWrapper v-else>
      <template #header>
        <AppBackButton
          v-if="stage === 'cart'"
          @click="selectedPlan = undefined"
        >
          {{ t('back') }}
        </AppBackButton>
        <DTIconButton
          v-else-if="stage === 'final'"
          variant="plain"
          @click="isOpen = false"
        >
          <LineXClose class="size-4" />
        </DTIconButton>

        <div
          class="
              pointer-events-none
              absolute
              inset-0
              flex
              select-none
              items-center
              justify-center
              "
        >
          <SvgHelper
            #default="{ attributes }"
            :width="96"
            :height="20"
          >
            <img
              :src="logoDocueRed"
              v-bind="attributes"
            >
          </SvgHelper>
        </div>
      </template>

      <SubscriptionCart v-if="stage === 'cart'" />
      <SubscriptionConfirmed
        v-else-if="stage === 'final' && selectedPlan"
        @exit-requested="isOpen = false"
      />
    </TopBarWrapper>
  </DTModal>
</template>
